// Taken from https://github.com/lelandrichardson/redux-injectable-store
import { combineReducers, createStore, Reducer, ReducersMapObject, Store, StoreEnhancer } from 'redux'

export interface StoreModule extends Store<any> {
  inject<State = {}>(namespace: string, reducer: Reducer<State> | ReducersMapObject): void
  injectAll(reducerMap: { [reducer: string]: Reducer<any> }): void
  clearReducers(): void
}
const isObjectMap = (val: any): val is ReducersMapObject => Object.keys(val).length > 0

const FAKE_INITIAL_REDUCER_NAMESPACE = '___'
const IDENTITY_REDUCER = (state = null) => state

const defaultWrapReducer = (reducer: Reducer<any>) => reducer

const makeEmptyReducerMap = () => ({
  // putting this here because `combineReducers` will complain if there isn't at least
  // one reducer initially.
  [FAKE_INITIAL_REDUCER_NAMESPACE]: IDENTITY_REDUCER,
})

export const createInjectableStore = <S = {}>(
  initialState: S,
  enhancer: StoreEnhancer<S>,
  wrapReducer = defaultWrapReducer
): StoreModule => {
  let reducers = makeEmptyReducerMap()
  const store = createStore(wrapReducer(combineReducers(reducers)), initialState, enhancer)

  const replace = (): void => {
    store.replaceReducer(wrapReducer(combineReducers(reducers)))
  }

  const clearReducers = (): void => {
    reducers = makeEmptyReducerMap()
  }

  const inject = <State = {}>(namespace: string, reducer: Reducer<State> | ReducersMapObject): void => {
    if (reducers[namespace] == null) {
      reducers[namespace] = isObjectMap(reducer) ? combineReducers(reducer) : reducer
      replace()
    }
  }

  const injectAll = (reducerMap: { [reducer: string]: Reducer<any> }): void => {
    let hasChanged = false
    Object.keys(reducerMap).forEach((namespace) => {
      if (reducers[namespace] == null) {
        reducers[namespace] = reducerMap[namespace]
        hasChanged = true
      }
    })
    if (hasChanged) {
      replace()
    }
  }

  return {
    ...store,
    inject,
    injectAll,
    clearReducers,
  }
}



// WEBPACK FOOTER //
// ./src/modules/store/create-async-store.ts